import type {
  Alert,
  Channel,
  ChannelTestBody,
  ChannelTestResponse,
  CreateChannelBody,
  DeliveryModePreview,
  Severity,
  CreateRouteBody,
  CreateMonitorBody,
  CreateSourceBody,
  CreateUserBody,
  MonitorCheckRecord,
  MonitorCheckResult,
  MonitorConfig,
  MonitorHistory,
  MonitorHistoryRange,
  MonitorsOverview,
  Delivery,
  ErrorCode,
  ErrorResponse,
  OverviewStats,
  Page,
  PreviewRoutingBody,
  PreviewRoutingResponse,
  Route,
  SessionResponse,
  Sound,
  Source,
  SourceWithToken,
  UpdateChannelBody,
  UpdateMonitorBody,
  UpdateRouteBody,
  UpdateSoundBody,
  UpdateSourceBody,
  UpdateUserBody,
  User,
} from '@meshalert/contract';

/**
 * The only place in the web app that knows about HTTP. Every function here is
 * typed from the shared contract, so an endpoint that changes shape breaks the
 * build rather than the console at 3am.
 */

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level messages, keyed by the path the API reported. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) out[detail.path] = detail.message;
    return out;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Eine Datei als Body, mit ihrem eigenen Content-Type statt JSON. */
  raw?: Blob;
  signal?: AbortSignal;
};

function requestInit(options: RequestOptions): Pick<RequestInit, 'headers' | 'body'> {
  if (options.raw) {
    return {
      headers: { 'content-type': options.raw.type || 'application/octet-stream' },
      body: options.raw,
    };
  }
  if (options.body === undefined) return { headers: {} };
  return { headers: { 'content-type': 'application/json' }, body: JSON.stringify(options.body) };
}

function parseJson(text: string): unknown {
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    // Ein Proxy vor der API antwortet bei Fehlern gern mit HTML.
    return null;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    ...requestInit(options),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = parseJson(text);

  if (!response.ok) {
    const envelope = payload as ErrorResponse | null;
    throw new ApiError(
      envelope?.error?.code ?? 'internal_error',
      envelope?.error?.message ?? `Anfrage fehlgeschlagen (${response.status})`,
      response.status,
      envelope?.error?.details,
    );
  }
  return payload as T;
}

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export const api = {
  session: {
    current: () => request<User>('/v1/users/me'),
    create: (body: { email: string; password: string }) =>
      request<SessionResponse>('/v1/sessions', { method: 'POST', body }),
    destroy: () => request<void>('/v1/sessions/current', { method: 'DELETE' }),
  },

  stats: {
    overview: () => request<OverviewStats>('/v1/stats/overview'),
    monitors: (params: { range: MonitorHistoryRange; tz: string }) =>
      request<MonitorsOverview>(`/v1/stats/monitors${query(params)}`),
  },

  alerts: {
    list: (params: {
      limit?: number;
      cursor?: string | null;
      status?: string;
      severity?: string;
      source_id?: string;
      q?: string;
    }) => request<Page<Alert>>(`/v1/alerts${query(params)}`),
    get: (id: string) => request<Alert>(`/v1/alerts/${id}`),
    acknowledge: (id: string) => request<Alert>(`/v1/alerts/${id}/acknowledge`, { method: 'POST' }),
    resolve: (id: string) => request<Alert>(`/v1/alerts/${id}/resolve`, { method: 'POST' }),
  },

  deliveries: {
    list: (params: { alert_id?: string; channel_id?: string; status?: string; limit?: number }) =>
      request<Page<Delivery>>(`/v1/deliveries${query(params)}`),
  },

  sources: {
    list: (params: { limit?: number; cursor?: string | null } = {}) =>
      request<Page<Source>>(`/v1/sources${query(params)}`),
    create: (body: CreateSourceBody) =>
      request<SourceWithToken>('/v1/sources', { method: 'POST', body }),
    update: (id: string, body: UpdateSourceBody) =>
      request<Source>(`/v1/sources/${id}`, { method: 'PATCH', body }),
    rotateToken: (id: string) =>
      request<SourceWithToken>(`/v1/sources/${id}/token`, { method: 'POST' }),
    remove: (id: string) => request<void>(`/v1/sources/${id}`, { method: 'DELETE' }),
  },

  monitors: {
    list: () => request<{ items: Source[] }>('/v1/monitors'),
    get: (id: string) => request<Source>(`/v1/monitors/${id}`),
    history: (id: string, params: { range: MonitorHistoryRange; tz: string }) =>
      request<MonitorHistory>(`/v1/monitors/${id}/history${query(params)}`),
    checks: (id: string, params: { limit?: number; cursor?: string | null; ok?: boolean }) =>
      request<Page<MonitorCheckRecord>>(`/v1/monitors/${id}/checks${query(params)}`),
    create: (body: CreateMonitorBody) => request<Source>('/v1/monitors', { method: 'POST', body }),
    update: (id: string, body: UpdateMonitorBody) =>
      request<Source>(`/v1/monitors/${id}`, { method: 'PATCH', body }),
    check: (id: string) =>
      request<MonitorCheckResult>(`/v1/monitors/${id}/check`, { method: 'POST' }),
    preview: (body: MonitorConfig) =>
      request<MonitorCheckResult>('/v1/monitors/preview', { method: 'POST', body }),
  },

  channels: {
    list: (params: { limit?: number; user_id?: string; kind?: string } = {}) =>
      request<Page<Channel>>(`/v1/channels${query(params)}`),
    create: (body: CreateChannelBody) => request<Channel>('/v1/channels', { method: 'POST', body }),
    update: (id: string, body: UpdateChannelBody) =>
      request<Channel>(`/v1/channels/${id}`, { method: 'PATCH', body }),
    remove: (id: string) => request<void>(`/v1/channels/${id}`, { method: 'DELETE' }),
    test: (id: string, body: ChannelTestBody = {}) =>
      request<ChannelTestResponse>(`/v1/channels/${id}/test`, { method: 'POST', body }),
  },

  devices: {
    previewMode: (id: string, severity: Severity) =>
      request<DeliveryModePreview>(`/v1/devices/${id}/delivery-mode${query({ severity })}`),
  },

  sounds: {
    list: () => request<Page<Sound>>('/v1/sounds'),
    upload: (name: string, file: File) =>
      request<Sound>(`/v1/sounds${query({ name })}`, { method: 'POST', raw: file }),
    update: (id: string, body: UpdateSoundBody) =>
      request<Sound>(`/v1/sounds/${id}`, { method: 'PATCH', body }),
    remove: (id: string) => request<void>(`/v1/sounds/${id}`, { method: 'DELETE' }),
    fileUrl: (id: string) => `/v1/sounds/${id}/file`,
  },

  routes: {
    list: (params: { limit?: number } = {}) => request<Page<Route>>(`/v1/routes${query(params)}`),
    create: (body: CreateRouteBody) => request<Route>('/v1/routes', { method: 'POST', body }),
    update: (id: string, body: UpdateRouteBody) =>
      request<Route>(`/v1/routes/${id}`, { method: 'PATCH', body }),
    remove: (id: string) => request<void>(`/v1/routes/${id}`, { method: 'DELETE' }),
    preview: (body: PreviewRoutingBody) =>
      request<PreviewRoutingResponse>('/v1/routes/preview', { method: 'POST', body }),
  },

  users: {
    list: (params: { limit?: number } = {}) => request<Page<User>>(`/v1/users${query(params)}`),
    create: (body: CreateUserBody) => request<User>('/v1/users', { method: 'POST', body }),
    update: (id: string, body: UpdateUserBody) =>
      request<User>(`/v1/users/${id}`, { method: 'PATCH', body }),
    remove: (id: string) => request<void>(`/v1/users/${id}`, { method: 'DELETE' }),
  },
};
